/* eslint-disable eqeqeq */
import { useContext, useEffect, useMemo, useState } from "react";
import { useTable } from "react-table";
import primaryComponents from "../../../primaryComponents";
import styles from "./styles.module.scss";
import { v4 as uuidv4 } from "uuid";
import { useImportCsv } from "../../../../hooks/useImportCsv";
import { useAddMultipleProducts } from "../../../../hooks/api/mutations/useInventory";
import { ToastContext } from "../../../../hooks/context";
import { useValidation } from "./useValidation";
import { DeleteIcon } from "../../../../assets/svgIcons";
import { useNavigate } from "react-router-dom";
import { pathConstants } from "../../../../routes/pathContants";
// import { useSelector } from "react-redux";
import {
  useGetProductCategoryList,
  useGetStockUnits,
} from "../../../../hooks/api/queries";

const inputType = {
  "Has Variants": "BOOLEAN",
  "Include VAT in price": "BOOLEAN",
  "Product category": "SELECT_CATEGORY",
  "Show product in sell mode": "BOOLEAN",
  "Stores ID": "SELECT_STORE",
  Unit: "SELECT_UNIT",
};

export const useBulkProductUploadForm = ({
  formData: uploadedFormdata = [],
}) => {
  const triggerToast = useContext(ToastContext);
  const [formData, setFormData] = useState(uploadedFormdata);
  const { convertDataToCSV } = useImportCsv({});
  const navigate = useNavigate();
  const [isUpdated, setIsUpdated] = useState(false);

  const {
    handleSubmit,
    control,
    trigger,
    setValue,
    formState: { errors },
  } = useValidation({ productItems: formData });

  // const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const { data: stockUnits, isLoading: isLoadingStockUnit } =
    useGetStockUnits();
  const { data: categories = [], isLoading: isLoadingCategories } =
    useGetProductCategoryList();

  const { mutate: uploadProduct, isLoading: isCreatingProduct } =
    useAddMultipleProducts();

  const handleBulkCreation = () => {
    const csv = convertDataToCSV(formData);
    const newFormData = new FormData();
    const csvBlob = new Blob([csv], { type: "text/csv" });

    newFormData.append("file", csvBlob, "data.csv");

    uploadProduct(newFormData, {
      onSuccess: () => {
        triggerToast("File uploaded successfully", "success");
        navigate(pathConstants.INVENTORY_PRODUCTS);
      },
    });
  };

  const deleteRow = (index) => {
    const newData = [...instance?.data];

    triggerToast("Product Removed", "success");
    newData.splice(index, 1);
    setFormData(newData);
    setIsUpdated((prev) => !prev);
  };

  const addRow = () => {
    const newData = {
      "Product name": "",
      "Product category": "",
      "Product sub-category": "",
      "SKU number": "",
      Barcode: "",
      Quantity: "",
      "Cost price": "",
      "Selling price": "",
      "Minimum stock quantity": 0,
      "Expiry date": "",
      Unit: "",
      "Stores ID": "",
      "Discount rate": "",
      "Wholesale selling price": "0",
      "Wholesale discount rate": "0",
      "Custom label": "",
      "Custom selling price": "",
      "Custom discount rate": "",
      "Show product in sell mode": true,
      "Has Variants": false,
      Variants: "",
      VAT: "0",
      "Include VAT in price": false,
    };

    setFormData((prev) => [newData, ...prev]);
    setIsUpdated((prev) => !prev);
    triggerToast("Added a new row", "success");
  };

  const columns = useMemo(() => {
    if (!formData?.length) return [];

    let tableColumns = [
      {
        Header: "Row",
        Cell: ({ row }) => (
          <span className="dark--text font-weight-bold">{row?.index + 1}</span>
        ),
      },
    ];

    const formColumns = Object.keys(formData?.[0])?.map((key) => ({
      Header: key,
      accessor: key,
      Cell: ({ cell: { value }, row }) => {
        const rowIndex = row.index;

        const updateValue = (value) => {
          const newData = [...instance?.data];
          newData[rowIndex][key] = value;
          setFormData(newData);
        };

        // BOOLEAN INPUT
        if (inputType[key] === "BOOLEAN") {
          const uuid = uuidv4();
          const checked = value === "TRUE" ? true : false;

          return (
            <span className="d-flex" style={{ gap: "10px" }}>
              <primaryComponents.RadioButton
                name={`${key}${uuid}`}
                id={`yes-${uuid}`}
                value={true}
                checked={checked === true}
                setRadioValue={() => updateValue(true)}
              >
                Yes
              </primaryComponents.RadioButton>

              <primaryComponents.RadioButton
                name={`${key}${uuid}`}
                id={`no-${uuid}`}
                value={false}
                checked={checked === false}
                setRadioValue={() => updateValue(false)}
              >
                No
              </primaryComponents.RadioButton>
            </span>
          );
        }

        if (inputType[key] === "SELECT_CATEGORY") {
          return (
            <span className={`selectPropMid small ${styles.selectPropMid}`}>
              <primaryComponents.SelectFieldV2
                control={control}
                name={`productItems.${rowIndex}.${key}`}
                borderColor={"#7647ee"}
                classNames={"white"}
                options={categories}
                nameKey={"name"}
                idKey="value"
                value={
                  categories?.find((category) => category?.name == value)
                    ?.name || value
                }
                errorMessage={errors?.productItems?.[rowIndex]?.[key]}
                onChange={(value, label) => updateValue(label)}
                floatError={false}
              />
            </span>
          );
        }

        // if (inputType[key] === "SELECT_STORE") {
        //   return (
        //     <span
        //       className={`selectPropMid small ${styles.selectPropMid}`}
        //       style={{ width: "200px" }}
        //     >
        //       <primaryComponents.MultipleSelectFieldV2
        //         control={control}
        //         name={`productItems.${rowIndex}.${key}`}
        //         borderColor={"#7647ee"}
        //         classNames={"white"}
        //         options={storeList}
        //         nameKey={"name"}
        //         idKey="value"
        //         // value={
        //         //   storeList?.find((store) => store?.id == value)?.name ||
        //         //   "Select"
        //         // }
        //         errorMessage={errors?.productItems?.[rowIndex]?.[key]}
        //         onChange={(value) => updateValue(value)}
        //         floatError={false}
        //       />
        //     </span>
        //   );
        // }

        if (inputType[key] === "SELECT_UNIT") {
          return (
            <span className={`selectPropMid small ${styles.selectPropMid}`}>
              <primaryComponents.SelectFieldV2
                control={control}
                name={`productItems.${rowIndex}.${key}`}
                borderColor={"#7647ee"}
                classNames={"white"}
                options={stockUnits}
                nameKey={"name"}
                idKey="value"
                value={
                  stockUnits?.find((stockUnit) => stockUnit?.unit == value)
                    ?.unit || value
                }
                errorMessage={errors?.productItems?.[rowIndex]?.[key]}
                onChange={(value) => updateValue(value)}
                floatError={false}
              />
            </span>
          );
        }

        // DEFAULT INPUT
        return (
          <primaryComponents.InputFieldV2
            control={control}
            name={`productItems.${rowIndex}.${key}`}
            classNames={`purple--border white ${styles.inputField}`}
            value={value}
            errorMessage={errors?.productItems?.[rowIndex]?.[key]}
            onKeyUp={() => {
              if (errors?.productItems?.[rowIndex]?.[key] !== undefined)
                trigger(`productItems.${rowIndex}.${key}`);
            }}
            onChange={({ target: { value } }) => updateValue(value)}
            floatError={false}
          />
        );
      },
    }));

    tableColumns = [
      ...tableColumns,
      ...formColumns,
      {
        Header: "Action",
        Cell: ({ row }) => {
          return (
            <span
              className="cursor-pointer"
              onClick={() => deleteRow(row?.index)}
            >
              <DeleteIcon />
            </span>
          );
        },
      },
    ];

    return tableColumns;
  }, [errors, isLoadingStockUnit, isLoadingCategories, isUpdated]);

  const instance = useTable({
    columns,
    data: formData || [],
    autoResetPage: false,
  });

  useEffect(() => {
    setValue("productItems", formData);
  }, [formData]);

  return {
    isCreatingProduct,
    instance,
    addRow,
    setFormData,
    handleBulkCreation,
    handleSubmit,
  };
};
